import { toast, useApiClient } from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { Event } from '@/config/objects/events';

/**
 * The realtime event settings an admin can flip while a hunt is running.
 * Each maps to a dedicated PATCH endpoint so the server can broadcast a
 * socket event after the write — mobile clients react within a frame
 * instead of waiting for the next poll.
 */
export const EVENT_REALTIME_SETTINGS = {
  leaderboardVisible: {
    endpoint: 'leaderboard-visibility',
    label: 'Leaderboard',
    defaultValue: true,
    onLabel: 'Visible to participants',
    offLabel: 'Hidden from participants',
    errorMessage: 'Failed to update leaderboard visibility',
  },
  teamChatEnabled: {
    endpoint: 'team-chat-enabled',
    label: 'Team Chat',
    defaultValue: true,
    onLabel: 'Visible to participants',
    offLabel: 'Hidden — silent mode',
    errorMessage: 'Failed to update team chat setting',
  },
  helpChatEnabled: {
    endpoint: 'help-chat-enabled',
    label: 'Help & Support',
    defaultValue: true,
    onLabel: 'Facilitator help enabled',
    offLabel: 'Help shortcut hidden',
    errorMessage: 'Failed to update help chat setting',
  },
  quizMode: {
    endpoint: 'quiz-mode',
    label: 'Quiz Mode',
    defaultValue: false,
    onLabel: 'No-map: challenges shown as a list',
    offLabel: 'Map mode (default)',
    errorMessage: 'Failed to update quiz mode',
  },
  // `endpoint: null` → plain event PUT; these settings only matter
  // server-side at submit time, so no dedicated broadcast endpoint.
  aiValidationEnabled: {
    endpoint: null,
    label: 'AI Photo Verdicts',
    defaultValue: false,
    onLabel: 'AI pre-checks each photo/video (uses tokens)',
    offLabel: 'Off — no AI spend',
    errorMessage: 'Failed to update AI verdict setting',
  },
  enforceTimeLimits: {
    endpoint: null,
    label: 'Challenge Time Limits',
    defaultValue: false,
    onLabel: 'Timed challenges expire when the clock runs out',
    offLabel: 'Timers are display-only',
    errorMessage: 'Failed to update time limit setting',
  },
  requireMediaReview: {
    endpoint: null,
    label: 'Review Before Points',
    defaultValue: false,
    onLabel: 'Photos/videos hold points until approved',
    offLabel: 'Points award instantly',
    errorMessage: 'Failed to update review setting',
  },
  // Corporate team play: the team is one device, not one per person.
  // Turning this on mid-event is safe — teams with no holder recorded keep
  // playing, and the first device to check in claims the team.
  requireSingleDevice: {
    endpoint: null,
    label: 'One Device Per Team',
    defaultValue: false,
    onLabel: 'Only the newest device to join can score',
    offLabel: 'Everyone on the team can play and score',
    errorMessage: 'Failed to update single-device setting',
  },
  // Corporate team play. Only refuses on a sustained, confidently measured
  // separation, and staff can grant a team a time-boxed pass from the Live
  // tab — so switching this on can't strand a team on a bad GPS fix.
  requireTeamTogether: {
    endpoint: null,
    label: 'Teams Must Stay Together',
    defaultValue: false,
    onLabel: 'Split teams cannot score until they regroup',
    offLabel: 'Teams can spread out freely',
    errorMessage: 'Failed to update stay-together setting',
  },
} as const;

export type EventRealtimeSetting = keyof typeof EVENT_REALTIME_SETTINGS;

type EventQueryData = { success: boolean; data: Event } | undefined;

/**
 * Optimistic toggle mutation for one realtime event setting: writes the
 * boolean to the setting's dedicated PATCH endpoint, patches the cached
 * Event immediately, and rolls back with a toast on failure.
 *
 * @param eventId - Target event id.
 * @param setting - Which realtime setting to toggle.
 * @returns TanStack mutation taking the new boolean value.
 */
export function useEventSettingMutation(
  eventId: string | undefined,
  setting: EventRealtimeSetting,
) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const config = EVENT_REALTIME_SETTINGS[setting];

  return useMutation({
    mutationFn: (value: boolean) =>
      config.endpoint
        ? apiClient.patch(`/events/${eventId}/${config.endpoint}`, { [setting]: value })
        : apiClient.put(`/events/${eventId}`, { [setting]: value }),
    onMutate: async (value: boolean) => {
      await queryClient.cancelQueries({ queryKey: ['Event', eventId] });
      const previous = queryClient.getQueryData(['Event', eventId]);
      queryClient.setQueryData(['Event', eventId], (old: EventQueryData) => {
        if (!old) return old;
        return { ...old, data: { ...old.data, [setting]: value } };
      });
      return { previous };
    },
    onError: (_err, _value, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['Event', eventId], context.previous);
      }
      toast.error(config.errorMessage);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['Event', eventId] });
    },
  });
}
